export type Unit = 'Meters' | 'Feet' | 'Inches' | 'Millimeters'

/**
 * Converts area from cm² to the target unit
 */
export const formatArea = (
  rawValue: number | undefined,
  currentUnit: Unit,
  locale: string
): string => {
  if (rawValue === undefined || rawValue === null) return 'N/A'

  const numericValue = rawValue
  if (isNaN(numericValue)) return 'N/A'

  // Convert from cm² to the target unit
  let convertedValue: number
  let unitLabel: string

  switch (currentUnit) {
    case 'Meters':
      // cm² to m² (1 m² = 10,000 cm²)
      convertedValue = numericValue / 10000
      unitLabel = 'm²'
      break
    case 'Feet':
      // cm² to ft² (1 ft² = 929.03 cm²)
      convertedValue = numericValue / 929.03
      unitLabel = 'ft²'
      break
    case 'Inches':
      // cm² to in² (1 in² = 6.4516 cm²)
      convertedValue = numericValue / 6.4516
      unitLabel = 'in²'
      break
    case 'Millimeters':
      // cm² to mm² (1 cm² = 100 mm²)
      convertedValue = numericValue * 100
      unitLabel = 'mm²'
      break
    default:
      // Default to m²
      convertedValue = numericValue / 10000
      unitLabel = 'm²'
  }

  // Format the number with proper locale formatting
  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}

// Extract the conversion logic
export const convertFromCentimeters = (cm: number, unit: Unit): number => {
  switch (unit) {
    case 'Meters':
      return cm / 100
    case 'Feet':
      return cm / 30.48
    case 'Inches':
      return cm / 2.54
    case 'Millimeters':
      return cm * 10
    default:
      return cm / 100
  }
}

// Update formatPerimeter to use the shared conversion logic
export const formatPerimeter = (
  rawValue: number | undefined,
  currentUnit: Unit,
  locale: string
): string => {
  if (rawValue === undefined || rawValue === null) return 'N/A'
  if (isNaN(rawValue)) return 'N/A'

  const convertedValue = convertFromCentimeters(rawValue, currentUnit)
  const unitLabel = getUnitLabel(currentUnit)

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}

/**
 * Converts Unreal units to a formatted length string in the target display unit.
 *
 * Conversion: Unreal units × measurementScale → centimeters → display unit
 */
export const formatLengthFromUnrealUnits = (
  rawValueUnreal: number | undefined,
  measurementScale: number,
  currentUnit: Unit,
  locale: string
): string => {
  if (rawValueUnreal === undefined || rawValueUnreal === null) return 'N/A'
  if (isNaN(rawValueUnreal)) return 'N/A'

  const valueInCm = rawValueUnreal * measurementScale
  const convertedValue = convertFromCentimeters(valueInCm, currentUnit)
  const unitLabel = getUnitLabel(currentUnit)

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}

export const getUnitLabel = (unit: Unit): string => {
  switch (unit) {
    case 'Meters':
      return 'm'
    case 'Feet':
      return 'ft'
    case 'Inches':
      return 'in'
    case 'Millimeters':
      return 'mm'
    default:
      return 'm'
  }
}

/**
 * Converts volume from cm³ to the target unit
 */
export const formatVolume = (
  rawValue: number | undefined,
  currentUnit: Unit,
  locale: string
): string => {
  if (rawValue === undefined || rawValue === null) return 'N/A'
  if (isNaN(rawValue)) return 'N/A'

  // Convert from cm³ to the target unit
  let convertedValue: number
  let unitLabel: string

  switch (currentUnit) {
    case 'Meters':
      // cm³ to m³ (1 m³ = 1,000,000 cm³)
      convertedValue = rawValue / 1000000
      unitLabel = 'm³'
      break
    case 'Feet':
      // cm³ to ft³ (1 ft³ = 28,316.8 cm³)
      convertedValue = rawValue / 28316.8
      unitLabel = 'ft³'
      break
    case 'Inches':
      // cm³ to in³ (1 in³ = 16.387 cm³)
      convertedValue = rawValue / 16.387
      unitLabel = 'in³'
      break
    case 'Millimeters':
      // cm³ to mm³ (1 cm³ = 1,000 mm³)
      convertedValue = rawValue * 1000
      unitLabel = 'mm³'
      break
    default:
      convertedValue = rawValue / 1000000
      unitLabel = 'm³'
  }

  // Format the number with proper locale formatting
  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(convertedValue)

  return `${formattedNumber} ${unitLabel}`
}
